// User Utility Functions

import { User, CreateUserData } from '../types/user';

/**
 * Format phone number to E.164 format
 * Example: (11) 98765-4321 -> +5511987654321
 */
export function formatPhoneToE164(phone: string, countryCode: string = '+55'): string {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // For Brazilian numbers
  if (countryCode === '+55') {
    // Mobile numbers: 11 digits (2 area code + 9 mobile)
    if (digits.length === 11) {
      return `+55${digits}`;
    }
    // Landline numbers: 10 digits (2 area code + 8 landline)
    if (digits.length === 10) {
      return `+55${digits}`;
    }
  }
  
  // Default: just add country code if not present
  if (digits.startsWith('55')) {
    return `+${digits}`;
  }
  
  return `${countryCode}${digits}`;
}

/**
 * Validate phone number format
 */
export function isValidPhone(phone: string): boolean {
  // E.164 format: + followed by up to 15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phone);
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate Brazilian CPF format (if needed)
 */
export function isValidCPF(cpf: string): boolean {
  // Remove all non-digit characters
  const digits = cpf.replace(/\D/g, '');
  
  // CPF must have 11 digits
  if (digits.length !== 11) return false;
  
  // Check for common invalid CPFs (all same digits)
  if (/^(\d)\1{10}$/.test(digits)) return false;
  
  // Validate CPF checksum
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (digit1 !== parseInt(digits[9])) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i);
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  return digit2 === parseInt(digits[10]);
}

/**
 * Format CPF for display
 */
export function formatCPF(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  
  return `${digits.substr(0, 3)}.${digits.substr(3, 3)}.${digits.substr(6, 3)}-${digits.substr(9, 2)}`;
}

/**
 * Format phone for display
 */
export function formatPhoneForDisplay(phone: string): string {
  // Remove country code for Brazilian numbers
  if (phone.startsWith('+55')) {
    const digits = phone.substring(3);
    if (digits.length === 11) {
      // Mobile: (11) 98765-4321
      return `(${digits.substr(0, 2)}) ${digits.substr(2, 5)}-${digits.substr(7, 4)}`;
    }
    if (digits.length === 10) {
      // Landline: (11) 8765-4321
      return `(${digits.substr(0, 2)}) ${digits.substr(2, 4)}-${digits.substr(6, 4)}`;
    }
  }
  
  return phone;
}

/**
 * Generate a default tenant ID based on organization info
 */
export function generateTenantId(organizationName: string): string {
  return organizationName
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '_')
    .substring(0, 20);
}

/**
 * Create user data with defaults
 */
export function createUserPayload(
  userData: Partial<CreateUserData>
): CreateUserData {
  const defaults: CreateUserData = {
    username: '',
    password: '',
    phone: '',
    email: '',
    full_name: '',
    role: 'user',
    language_preference: 'pt',
    timezone: 'America/Sao_Paulo',
    isActive: true,
  };

  return { ...defaults, ...userData } as CreateUserData;
}

/**
 * Extract user display name
 */
export function getUserDisplayName(user: User): string {
  return user.full_name || user.email || user.phone || 'Usuário';
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(user: User): string {
  const name = getUserDisplayName(user);
  const words = name.split(' ');
  
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  
  return name.substring(0, 2).toUpperCase();
}

/**
 * Check if user has specific role
 */
export function userHasRole(user: User, roleKey: string): boolean {
  return user.role === roleKey;
}

/**
 * Check if user has permission for specific farm
 */
export function userHasFarmRole(user: User, farmId: string, roleKey: string): boolean {
  if (!user.farm_role_assignments) return false;
  
  const assignment = user.farm_role_assignments.find(
    assignment => assignment.farm_id === farmId
  );
  
  return assignment ? assignment.role_keys.includes(roleKey) : false;
}

/**
 * Get user's farm roles
 */
export function getUserFarmRoles(user: User, farmId: string): string[] {
  if (!user.farm_role_assignments) return [];
  
  const assignment = user.farm_role_assignments.find(
    assignment => assignment.farm_id === farmId
  );
  
  return assignment ? assignment.role_keys : [];
}

/**
 * Validate user data before creation
 */
export function validateUserData(userData: Partial<CreateUserData>): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!userData.email || !isValidEmail(userData.email)) {
    errors.push('Email inválido');
  }

  if (!userData.phone || !isValidPhone(userData.phone)) {
    errors.push('Telefone inválido');
  }

  if (!userData.full_name || userData.full_name.trim().length < 2) {
    errors.push('Nome completo é obrigatório');
  }



  return {
    isValid: errors.length === 0,
    errors,
  };
}